import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import qs from 'qs';

import {
    INCOMING_PAYMENT_REQUESTS_CACHE,
    PAYMENT_REQUESTS_CACHE,
    PAYMENT_REQUEST_CACHE,
    PAYMENT_REQUEST_PUBLIC_CACHE,
} from 'constants/cache';
import {useClient} from 'context/auth-context';
import {VirtualIban} from 'api/wallets';
import {BusinessPartner, IContact, ITag} from 'types/api';
import {IDocument} from 'types/documents';
import {Transaction} from 'types/transactions';
import {CollectionVariants} from 'components/pages/CollectionsPage/useCollectionsPage';
import {IAmnisApiFilters} from 'components/ui/AmnisFilters/types';
import {PATCH_HEADERS} from './constants';
import {getIdsFromTags} from './util';

export type PaymentRequestDocument = Pick<
    IDocument,
    '@id' | 'type' | 'originalFileName'
>;

export interface IPaymentRequest {
    '@context': string;
    '@id': string;
    '@type': 'PaymentRequest';
    amount: number;
    businessPartner?: string;
    contact: IContact;
    currency: string;
    documents: PaymentRequestDocument[];
    dueDate: string;
    draft: boolean;
    email: string;
    hash: string;
    invoiceNumber: string | null;
    message: string | null;
    needAutomaticReminder: boolean;
    pending: boolean;
    scheduleDate: string;
    transaction: Transaction | null;
    discountDays?: number;
    discountPercent?: number;
    todayAmount: number;
    todayDiscountPercent: number;
    tags: ITag[];
}

export interface IGetPaymentRequestsInfiniteResponse {
    '@context': '/web_api/contexts/PaymentRequest';
    '@id': string;
    '@type': 'hydra:Collection';
    'hydra:member': IPaymentRequest[];
    'hydra:totalItems': number;
}

export interface IUseGetPaymentRequestsFilters extends IAmnisApiFilters {
    pending?: boolean;
}

const useGetPaymentRequestsInfinite = (
    filters: IUseGetPaymentRequestsFilters = {},
    itemsPerPage = 30,
) => {
    const client = useClient();
    const {amount, scheduleDate, dueDate, ...rest} = filters;

    return useInfiniteQuery<IGetPaymentRequestsInfiniteResponse>({
        queryKey: [PAYMENT_REQUESTS_CACHE, {filters}],
        queryFn: ({pageParam}) => {
            return client(
                `/web_api/payment_requests?`,
                {
                    params: {
                        ...rest,
                        page: pageParam,
                        itemsPerPage,
                        'amount[gte]': amount?.abs_gte,
                        'amount[lte]': amount?.abs_lte,
                        'scheduleDate[after]': scheduleDate,
                        'dueDate[before]': dueDate,
                    },
                },
                true,
            );
        },
        initialPageParam: 1,
        getNextPageParam: (lastGroup, allGroups) => {
            if (
                lastGroup['hydra:member'].length === itemsPerPage &&
                allGroups.length * itemsPerPage < lastGroup['hydra:totalItems']
            ) {
                return allGroups.length + 1;
            }
            return undefined;
        },
    });
};
export interface IGetIncomingPaymentRequestsResponse {
    '@context': '/web_api/contexts/PaymentRequest';
    '@id': string;
    '@type': 'hydra:Collection';
    'hydra:member': IPaymentRequest[];
    'hydra:totalItems': number;
}

export interface IUseGetIncomingPaymentRequestsFilters {
    pending?: boolean;
}

const useGetIncomingPaymentRequestsInfinite = (
    businessPartner: BusinessPartner,
    filters: IUseGetIncomingPaymentRequestsFilters = {},
    itemsPerPage = 30,
) => {
    const client = useClient();
    const queryString = qs.stringify(filters, {skipNulls: true, encode: false});

    return useInfiniteQuery<IGetIncomingPaymentRequestsResponse>({
        queryKey: [
            INCOMING_PAYMENT_REQUESTS_CACHE,
            {bpId: businessPartner['@id'], queryString},
        ],
        queryFn: ({pageParam}) => {
            return client(
                `/web_api/incoming_payment_requests?${queryString}`,
                {
                    params: {
                        business_partner: businessPartner['@id'],
                        page: pageParam,
                        itemsPerPage,
                    },
                },
                true,
            );
        },
        initialPageParam: 1,
        getNextPageParam: (lastGroup, allGroups) => {
            if (
                lastGroup['hydra:member'].length === itemsPerPage &&
                allGroups.length * itemsPerPage < lastGroup['hydra:totalItems']
            ) {
                return allGroups.length + 1;
            }
            return undefined;
        },
    });
};

const useGetPaymentRequest = (paymentRequestId?: string | null) => {
    const client = useClient();

    const id = paymentRequestId?.split('/').pop() || '';

    return useQuery<IPaymentRequest>({
        queryKey: [PAYMENT_REQUEST_CACHE, {paymentRequestId}],
        queryFn: () => client(`/web_api/payment_requests/${id}`, {}),
        enabled: !!id,
    });
};

const useGetIncomingPaymentRequest = (
    businessPartner: BusinessPartner,
    variant: CollectionVariants,
    paymentRequestId?: string,
) => {
    const client = useClient();

    const id = paymentRequestId?.split('/').pop() || '';
    const params = new URLSearchParams({
        business_partner: businessPartner['@id'],
    });

    return useQuery<IPaymentRequest>({
        queryKey: [
            PAYMENT_REQUEST_CACHE,
            {paymentRequestId, variant, businessPartner},
        ],
        queryFn: () =>
            client(
                `/web_api/incoming_payment_requests/${id}?${params.toString()}`,
                {},
            ),
        enabled: !!id && !!businessPartner && variant === 'incoming',
    });
};

export interface IUseCreatePaymentRequest {
    businessPartner: string;
    contact: string;
    currency: string;
    amount: string;
    scheduleDate: string;
    dueDate: string;
    email: string;
    message: string | null;
    needAutomaticReminder: boolean;
    invoiceNumber: string | null;
    discountDays: number | null;
    discountPercent: number | null;
    tags: ITag[];
}

const useCreatePaymentRequest = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<IPaymentRequest, unknown, IUseCreatePaymentRequest>({
        mutationFn: (data: any) =>
            client('/web_api/payment_requests', {
                data: {...data, tags: getIdsFromTags(data.tags)},
                method: 'POST',
            }),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_REQUESTS_CACHE],
            }),
    });
};

const useDeletePaymentRequest = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (paymentRequestIri: string) =>
            client(paymentRequestIri, {
                data: {},
                method: 'DELETE',
            }),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_REQUESTS_CACHE],
            }),
    });
};

export interface IUseUploadPaymentRequestDocument {
    file: File;
    paymentRequestId: string;
}

const useUploadPaymentRequestDocument = () => {
    const client = useClient();

    return useMutation<IDocument, unknown, IUseUploadPaymentRequestDocument>({
        mutationFn: async ({file, paymentRequestId}) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', 'payment_request');

            return client(`${paymentRequestId}/documents/upload`, {
                data: formData,
                method: 'POST',
                headers: {'Content-Type': 'multipart/form-data'},
                keepOriginal: true,
            });
        },
    });
};

const useDeletePaymentRequestDocument = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<
        unknown,
        unknown,
        {paymentRequest: IPaymentRequest; documentId: string}
    >({
        mutationFn: ({paymentRequest, documentId}) => {
            queryClient.cancelQueries({queryKey: [PAYMENT_REQUEST_CACHE]});
            queryClient.setQueryData(
                [
                    PAYMENT_REQUEST_CACHE,
                    {
                        paymentRequestId: paymentRequest['@id'],
                    },
                ],
                (prev: IPaymentRequest) => ({
                    ...prev,
                    documents: [
                        ...prev.documents.filter(
                            doc => doc['@id'] !== documentId,
                        ),
                    ],
                }),
            );
            return client(
                `${paymentRequest['@id']}/documents/${documentId
                    .split('/')
                    .pop()}`,
                {
                    data: {},
                    method: 'DELETE',
                },
            );
        },
        onSettled: () =>
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_REQUESTS_CACHE],
            }),
    });
};

const useActivatePaymentRequest = () => {
    const client = useClient();

    return useMutation({
        mutationFn: (paymentRequestId: string) =>
            client(`${paymentRequestId}/activate`, {
                data: {},
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/vnd.api+json',
                },
            }),
    });
};

const useUpdatePaymentRequest = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<
        IPaymentRequest,
        unknown,
        Partial<IUseCreatePaymentRequest> & {'@id': string}
    >({
        mutationFn: paymentRequest =>
            client(`${paymentRequest['@id']}`, {
                data: {
                    ...paymentRequest,
                    tags: getIdsFromTags(paymentRequest.tags || []),
                },
                method: 'PATCH',
                ...PATCH_HEADERS,
            }),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_REQUEST_CACHE],
            }),
    });
};

export interface IPaymentRequestPublic
    extends Omit<IPaymentRequest, 'businessPartner'> {
    businessPartner: Pick<
        BusinessPartner,
        | '@id'
        | '@type'
        | 'companyName'
        | 'address'
        | 'city'
        | 'countryOfIncorporation'
        | 'zip'
        | 'currencyCloudEnabled'
    >;
    virtualIbans: VirtualIban[];
    senderContact: Pick<
        IContact,
        | '@id'
        | '@type'
        | 'connectedBusinessPartner'
        | 'country'
        | 'id'
        | 'name'
        | 'type'
    > | null;
}

const useGetPaymentRequestPublic = (
    hash: string | null,
    activeBp?: BusinessPartner,
) => {
    const client = useClient();

    return useQuery<IPaymentRequestPublic>({
        queryKey: [PAYMENT_REQUEST_PUBLIC_CACHE, {hash}],
        queryFn: () =>
            client(
                `/web_api/payment_requests_public/${hash}${
                    activeBp
                        ? `?active_business_partner=${activeBp['@id']}`
                        : ''
                }`,
                {},
            ),
        enabled: !!hash,
    });
};

export interface IUseConnectPaymentRequestSenderRequest {
    requestId: string;
    activeBusinessPartner: BusinessPartner;
}

const useConnectPaymentRequestSender = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<
        unknown,
        unknown,
        IUseConnectPaymentRequestSenderRequest
    >({
        mutationFn: ({requestId, activeBusinessPartner}) =>
            client(
                `/web_api/payment_requests/${requestId
                    .split('/')
                    .pop()}/connect`,
                {
                    data: {
                        connectBusinessPartner: activeBusinessPartner['@id'],
                    },
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/vnd.api+json',
                    },
                },
            ),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_REQUEST_PUBLIC_CACHE],
            }),
    });
};

export interface IUseReconcilePaymentRequestWithTransaction {
    paymentRequestId: string;
    transactionId: string;
}

const useReconcilePaymentRequestWithTransaction = () => {
    const client = useClient();

    return useMutation({
        mutationFn: ({
            paymentRequestId,
            transactionId,
        }: IUseReconcilePaymentRequestWithTransaction) => {
            return client(
                `${paymentRequestId}/reconcile/${transactionId
                    .split('/')
                    .pop()}`,
                {
                    data: {},
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/vnd.api+json',
                    },
                },
            );
        },
    });
};

export interface IUseUnReconcilePaymentRequest {
    paymentRequestId: string;
}

const useUnReconcilePaymentRequest = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({paymentRequestId}: IUseUnReconcilePaymentRequest) => {
            return client(`${paymentRequestId}/unreconcile`, {
                data: {},
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/vnd.api+json',
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_REQUEST_CACHE],
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_REQUESTS_CACHE],
            });
        },
    });
};

export {
    useGetPaymentRequestsInfinite,
    useCreatePaymentRequest,
    useUploadPaymentRequestDocument,
    useGetPaymentRequest,
    useGetPaymentRequestPublic,
    useReconcilePaymentRequestWithTransaction,
    useDeletePaymentRequest,
    useActivatePaymentRequest,
    useConnectPaymentRequestSender,
    useUpdatePaymentRequest,
    useGetIncomingPaymentRequestsInfinite,
    useGetIncomingPaymentRequest,
    useDeletePaymentRequestDocument,
    useUnReconcilePaymentRequest,
};
